import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Plus,
  Save,
  RotateCcw,
  User,
  MapPin,
  Phone,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import autoIncrementService from '../../services/autoIncrementService'
import paymentTermsMasterService from '../../services/paymentTermsMasterService'
import customerMasterService from '../../services/customerMasterService'
import { getStateOptions } from '../../constants/indianStates'
import '../Accounts/PaymentForm.css'

const CustomerMasterForm = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('customerInfo')
  const [showHelp, setShowHelp] = useState(false)
  const [isLoadingCustomerCode, setIsLoadingCustomerCode] = useState(false)
  const [paymentTermsOptions, setPaymentTermsOptions] = useState([])
  const [formData, setFormData] = useState({
    // Customer Info - Matching backend model
    customerCode: '',
    customerName: '', // Backend: customerName
    customerNickName: '',
    customerCategory: '',
    customerType: '',
    region: '', // Required field
    customerUdyogAadhar: '',
    customerPAN: '', // Backend: customerPAN
    GSTIN: '', // Backend: GSTIN
    GSTClassification: '', // Backend: GSTClassification
    creditLimit: '',
    customerPaymentTerms: '', // Backend: customerPaymentTerms
    customerCurrency: 'INR', // Backend: customerCurrency
    frightCharge: '',
    transporter: '',
    destination: '',
    customerWebsite: '',
    eximCode: '',
    transporterGSTIN: '',
    transporterId: '',
    categoryType: 'Domestic',
    isCustomerActive: 'A', // Backend: isCustomerActive
    // Legacy fields for form compatibility
    customerNameOnTaxInvoice: '',
    panCardNo: '',
    paymentTerms: '',
    gstin: '',
    gstClassification: '',
    currency: '',
    status: 'Active'
  })

  const [addresses, setAddresses] = useState([
    {
      id: 1,
      country: '',
      stateProvince: '',
      cityDistrict: '',
      pinCode: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: ''
    }
  ])

  const [contacts, setContacts] = useState([
    {
      id: 1,
      contactPerson: '',
      email: '',
      mobile: '',
      alternatePhone: '',
      department: '',
      designation: ''
    }
  ])

  // Fetch customer code on component mount
  useEffect(() => {
    fetchCustomerCode()
    fetchPaymentTerms()
  }, [])

  const fetchCustomerCode = async () => {
    try {
      setIsLoadingCustomerCode(true)
      const response = await autoIncrementService.getCurrentValue('CUSTOMER')
      
      if (response && response.success) {
        const { data } = response
        if (data && data.value !== undefined) {
          // For display purposes, show the NEXT value that will be assigned
          // If currentValue is 2, the next customer should get value 2 (formatted as C0002)
          const currentValue = data.value || 0
          const nextValue = currentValue
          
          // Use the formattedValue from backend to extract prefix and digits
          let prefix = 'C'
          let digits = 4
          
          if (data.formattedValue) {
            // Extract prefix and digits from the current formatted value
            // e.g., "C0002" -> prefix="C", digits=4
            const match = data.formattedValue.match(/^([A-Za-z]+)(\d+)$/)
            if (match) {
              prefix = match[1]
              digits = match[2].length
            }
          }
          
          const paddedValue = nextValue.toString().padStart(digits, '0')
          const formattedValue = `${prefix}${paddedValue}`
          
          console.log('Auto-increment debug:', {
            currentValue: data.value,
            nextValue,
            prefix,
            digits,
            formattedValue
          })
          
          setFormData(prev => ({
            ...prev,
            customerCode: formattedValue
          }))
        } else {
          // Fallback if backend doesn't return expected structure
          const currentValue = data?.value || 0
          const nextValue = currentValue
          const paddedValue = nextValue.toString().padStart(4, '0')
          const formattedValue = `C${paddedValue}`
          
          setFormData(prev => ({
            ...prev,
            customerCode: formattedValue
          }))
        }
      } else {
        // Fallback customer code
        setFormData(prev => ({
          ...prev,
          customerCode: 'C0001'
        }))
      }
    } catch (error) {
      console.error('Error fetching customer code:', error)
      // Set fallback customer code
      setFormData(prev => ({
        ...prev,
        customerCode: 'C0001'
      }))
    } finally {
      setIsLoadingCustomerCode(false)
    }
  }

  const fetchPaymentTerms = async () => {
    try {
      const response = await paymentTermsMasterService.getActivePaymentTerms()
      if (response && response.success) {
        setPaymentTermsOptions(response.data || [])
      }
    } catch (error) {
      console.error('Error fetching payment terms:', error)
      toast.error('Failed to load payment terms')
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleAddressChange = (addressId, field, value) => {
    setAddresses(prev => 
      prev.map(addr => 
        addr.id === addressId 
          ? { ...addr, [field]: value }
          : addr
      )
    )
  }

  const addAddress = () => {
    const newAddress = {
      id: Date.now(),
      country: '',
      stateProvince: '',
      cityDistrict: '',
      pinCode: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: ''
    }
    setAddresses(prev => [...prev, newAddress])
  }

  const removeAddress = (addressId) => {
    if (addresses.length > 1) {
      setAddresses(prev => prev.filter(addr => addr.id !== addressId))
    }
  }

  const handleContactChange = (contactId, field, value) => {
    setContacts(prev => 
      prev.map(contact => 
        contact.id === contactId 
          ? { ...contact, [field]: value }
          : contact
      )
    )
  }

  const addContact = () => {
    const newContact = {
      id: Date.now(),
      contactPerson: '',
      email: '',
      mobile: '',
      alternatePhone: '',
      department: '',
      designation: ''
    }
    setContacts(prev => [...prev, newContact])
  }

  const removeContact = (contactId) => {
    if (contacts.length > 1) {
      setContacts(prev => prev.filter(contact => contact.id !== contactId))
    }
  }

  const handleSave = async () => {
    try {
      // Get the next customer code
      const customerCodeResponse = await autoIncrementService.getNextValue('CUSTOMER')
      if (!customerCodeResponse || !customerCodeResponse.success) {
        toast.error('Failed to generate customer code')
        return
      }

      const customerCode = customerCodeResponse.data?.sequence?.formattedValue || formData.customerCode

      // Prepare customer data - matching backend model exactly
      const customerData = {
        customerCode: customerCode,
        customerName: formData.customerName,
        customerNickName: formData.customerNickName,
        customerCategory: formData.customerCategory,
        customerType: formData.customerType,
        region: formData.region,
        customerUdyogAadhar: formData.customerUdyogAadhar,
        customerPAN: formData.customerPAN,
        GSTIN: formData.GSTIN,
        GSTClassification: formData.GSTClassification,
        customerBillingAddress: addresses.map(addr => ({
          line1: addr.addressLine1,
          line2: addr.addressLine2,
          line3: addr.addressLine3,
          line4: addr.addressLine4,
          state: addr.stateProvince,
          city: addr.cityDistrict,
          pinCode: addr.pinCode,
          country: addr.country,
          contactPersonName: contacts[0]?.contactPerson || '',
          contactPersonNumber: contacts[0]?.mobile || ''
        })),
        customerShippingAddress: addresses.map(addr => ({
          line1: addr.addressLine1,
          line2: addr.addressLine2,
          line3: addr.addressLine3,
          state: addr.stateProvince,
          city: addr.cityDistrict,
          pinCode: addr.pinCode,
          country: addr.country,
          contactPersonName: contacts[0]?.contactPerson || '',
          contactPersonNumber: contacts[0]?.mobile || ''
        })),
        customerContactInfo: contacts.map(contact => ({
          contactPersonName: contact.contactPerson,
          contactPersonDesignation: contact.designation,
          contactPersonDepartment: contact.department,
          contactPersonNumber: contact.mobile,
          contactPersonEmail: contact.email
        })),
        frightCharge: formData.frightCharge,
        transporter: formData.transporter,
        destination: formData.destination,
        customerCurrency: formData.customerCurrency,
        creditLimit: formData.creditLimit,
        customerPaymentTerms: formData.customerPaymentTerms,
        isCustomerActive: formData.isCustomerActive,
        customerWebsite: formData.customerWebsite,
        customerBankDetails: [], // Empty array for now
        eximCode: formData.eximCode,
        transporterGSTIN: formData.transporterGSTIN,
        transporterId: formData.transporterId,
        categoryType: formData.categoryType
      }

      // Create customer
      const response = await customerMasterService.createCustomer(customerData)
      
      if (response && response.success) {
        toast.success('Customer created successfully!')
        navigate('/sales/customer-master')
      } else {
        toast.error(response?.message || 'Failed to create customer')
      }
    } catch (error) {
      console.error('Error creating customer:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
        navigate('/login')
      } else {
        toast.error('Failed to create customer')
      }
    }
  }

  const handleReset = () => {
    setFormData({
      customerCategory: '',
      gstClassification: '',
      creditLimit: '',
      customerCode: '',
      gstin: '',
      paymentTerms: '',
      customerNameOnTaxInvoice: '',
      panCardNo: '',
      freightTerms: '',
      customerVendorCode: '',
      currency: '',
      status: 'Active',
      contactPerson: '',
      email: '',
      phone: '',
      mobile: '',
      website: '',
      alternateEmail: '',
      alternatePhone: ''
    })
    setAddresses([{
      id: 1,
      country: '',
      stateProvince: '',
      cityDistrict: '',
      pinCode: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: ''
    }])
    setContacts([{
      id: 1,
      contactPerson: '',
      email: '',
      mobile: '',
      alternatePhone: '',
      department: '',
      designation: ''
    }])
  }

  const handleBack = () => {
    navigate('/sales/customer-master')
  }

  const handleTest = () => {
    // Populate test data for form fields
    setFormData({
      customerCode: formData.customerCode, // Keep the auto-generated code
      customerName: 'Test Customer Private Limited',
      customerNickName: 'Test Customer',
      customerCategory: 'Domestic',
      customerType: 'Corporate',
      region: 'South',
      customerUdyogAadhar: 'UDYAM-TEST-123456',
      customerPAN: 'ABCDE1234F',
      GSTIN: '27ABCDE1234F1Z5',
      GSTClassification: 'Regular',
      creditLimit: '100000',
      customerPaymentTerms: paymentTermsOptions.length > 0 ? paymentTermsOptions[0].description : '30 Days',
      customerCurrency: 'INR',
      frightCharge: '500',
      transporter: 'Test Transporter',
      destination: 'Mumbai',
      customerWebsite: 'https://www.testcustomer.com',
      eximCode: 'EXIM123456',
      transporterGSTIN: '27TRANSPORTER1234F1Z5',
      transporterId: 'TRANS001',
      categoryType: 'Domestic',
      isCustomerActive: 'A',
      // Legacy fields
      customerNameOnTaxInvoice: 'Test Customer Private Limited',
      panCardNo: 'ABCDE1234F',
      paymentTerms: paymentTermsOptions.length > 0 ? paymentTermsOptions[0].description : '30 Days',
      gstin: '27ABCDE1234F1Z5',
      gstClassification: 'Regular',
      currency: 'INR',
      status: 'Active'
    })

    // Populate test address data
    setAddresses([{
      id: 1,
      country: 'India',
      stateProvince: 'Maharashtra',
      cityDistrict: 'Mumbai',
      pinCode: '400001',
      addressLine1: '123 Test Street',
      addressLine2: 'Test Building, Floor 5',
      addressLine3: 'Test Area',
      addressLine4: 'Near Test Landmark'
    }])

    // Populate test contact data
    setContacts([{
      id: 1,
      contactPerson: 'John Doe',
      email: 'john.doe@testcustomer.com',
      mobile: '9876543210',
      alternatePhone: '9876543211',
      department: 'Sales',
      designation: 'Sales Manager'
    }])

    toast.success('Test data populated successfully!')
  }

  // Listen for F1 key to populate test data
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Check if F1 key is pressed (keyCode 112 or key === 'F1')
      if (event.key === 'F1' || event.keyCode === 112) {
        event.preventDefault() // Prevent browser help menu
        handleTest()
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyPress)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [paymentTermsOptions, formData.customerCode]) // Include dependencies used by handleTest

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Masters',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Customer Master',
      path: '/sales/customer-master',
      isCurrent: true
    }
  ]

  const helpContent = (
    <>
      <h3>What is Customer Master?</h3>
      <p>Customer Master lets you <strong>create and manage customer information</strong>—code, name, category, region, GST/tax details, payment terms, addresses, and contacts.</p>
      <h3>Tabs</h3>
      <p><strong>Customer Info</strong> – Code, name, category, type, region, Udyog Aadhar, PAN, GSTIN, credit limit, payment terms, currency, and status.</p>
      <p><strong>Address</strong> – One or more addresses (country, state, city, pin, address lines).</p>
      <p><strong>Contact</strong> – Contact persons with email, mobile, department, designation.</p>
      <h3>How to use</h3>
      <p>Fill required fields (e.g. customer name, region). Use <strong>Save</strong> to create. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Customer Master Entry</h1>
          <p>Create and manage customer information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container pay-entry">
        <div className="sac-form-header">
          <h2>Customer Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'customerInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('customerInfo')}
          >
            <User size={18} />
            <span>Customer Info</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'customerAddress' ? 'active' : ''}`}
            onClick={() => setActiveTab('customerAddress')}
          >
            <MapPin size={18} />
            <span>Customer Address</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'contactDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('contactDetails')}
          >
            <Phone size={18} />
            <span>Contact Details</span>
          </button>
        </div>
          
        <form className="form-content">
          {/* Customer Info Tab */}
          {activeTab === 'customerInfo' && (
            <div className="tab-content">
              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Customer</h3>
                    <p>Who this customer is, and where they sit in the business.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-4">
                <div className="form-group">
                  <label htmlFor="customerCategory" className="form-label required">
                    Customer Category
                  </label>
                  <select
                    id="customerCategory"
                    name="customerCategory"
                    value={formData.customerCategory}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select Type</option>
                    <option value="Domestic">Domestic</option>
                    <option value="International">International</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="customerCode" className="form-label required">
                    Customer Code
                  </label>
                  <input
                    type="text"
                    id="customerCode"
                    name="customerCode"
                    value={isLoadingCustomerCode ? 'Generating...' : formData.customerCode}
                    readOnly
                    className="form-input pay-readonly"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerName" className="form-label required">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    id="customerName"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter customer name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="region" className="form-label required">
                    Region
                  </label>
                  <select
                    id="region"
                    name="region"
                    value={formData.region}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select Region</option>
                    <option value="North">North</option>
                    <option value="South">South</option>
                    <option value="East">East</option>
                    <option value="West">West</option>
                    <option value="Central">Central</option>
                  </select>
                </div>
              </div>
              </section>

              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Tax</h3>
                    <p>GST and PAN, and the currency this customer is billed in.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-4">
                <div className="form-group">
                  <label htmlFor="GSTClassification" className="form-label required">
                    GST Classification
                  </label>
                  <select
                    id="GSTClassification"
                    name="GSTClassification"
                    value={formData.GSTClassification}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">GST Classification</option>
                    <option value="Regular">Regular</option>
                    <option value="Composition">Composition</option>
                    <option value="Unregistered">Unregistered</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="GSTIN" className="form-label required">
                    GSTIN
                  </label>
                  <input
                    type="text"
                    id="GSTIN"
                    name="GSTIN"
                    value={formData.GSTIN}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter GSTIN"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerPAN" className="form-label required">
                    PAN Card No.
                  </label>
                  <input
                    type="text"
                    id="customerPAN"
                    name="customerPAN"
                    value={formData.customerPAN}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter PAN number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerCurrency" className="form-label required">
                    Currency
                  </label>
                  <select
                    id="customerCurrency"
                    name="customerCurrency"
                    value={formData.customerCurrency}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select</option>
                    <option value="INR">INR - Indian Rupee</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                  </select>
                </div>
              </div>
              </section>

              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Terms</h3>
                    <p>Credit, payment terms, freight, and whether the customer is active.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-4">
                <div className="form-group">
                  <label htmlFor="creditLimit" className="form-label">
                    Credit Limit
                  </label>
                  <input
                    type="number"
                    id="creditLimit"
                    name="creditLimit"
                    value={formData.creditLimit}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerPaymentTerms" className="form-label required">
                    Payment Terms
                  </label>
                  <select
                    id="customerPaymentTerms"
                    name="customerPaymentTerms"
                    value={formData.customerPaymentTerms}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select Payment Terms</option>
                    {paymentTermsOptions.map((term) => (
                      <option key={term._id} value={term.description}>
                        {term.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="freightTerms" className="form-label">
                    Freight Terms
                  </label>
                  <select
                    id="freightTerms"
                    name="freightTerms"
                    value={formData.freightTerms}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select Freight Terms</option>
                    <option value="FOB">FOB</option>
                    <option value="CIF">CIF</option>
                    <option value="EXW">EXW</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="status" className="form-label required">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
              </section>
            </div>
          )}

          {/* Customer Address Tab */}
          {activeTab === 'customerAddress' && (
            <div className="tab-content">
              {addresses.map((address, index) => (
                <section key={address.id} className="pay-block">
                  <div className="pay-block-head">
                    <div>
                      <h3>{index === 0 ? 'Primary address' : `Address ${index + 1}`}</h3>
                      <p>{index === 0 ? 'The main billing address. Add another if this customer has more than one.' : 'An additional address for this customer.'}</p>
                    </div>
                    {index === 0 && (
                      <button 
                        type="button" 
                        className="add-address-button"
                        onClick={addAddress}
                      >
                        <Plus size={16} />
                      </button>
                    )}
                    {index > 0 && (
                      <button 
                        type="button" 
                        className="remove-address-button"
                        onClick={() => removeAddress(address.id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="pay-grid pay-grid-4">
                    <div className="form-group">
                      <label className="form-label required">Country</label>
                      <select
                        value={address.country}
                        onChange={(e) => handleAddressChange(address.id, 'country', e.target.value)}
                        className="form-input"
                      >
                        <option value="">Select Country</option>
                        <option value="India">India</option>
                        <option value="USA">USA</option>
                        <option value="UK">UK</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">State/Province</label>
                      <select
                        value={address.stateProvince}
                        onChange={(e) => handleAddressChange(address.id, 'stateProvince', e.target.value)}
                        className="form-input"
                      >
                        <option value="">Select State</option>
                        {getStateOptions().map(state => (
                          <option key={state.value} value={state.value}>
                            {state.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">City/District</label>
                      <input
                        type="text"
                        value={address.cityDistrict}
                        onChange={(e) => handleAddressChange(address.id, 'cityDistrict', e.target.value)}
                        className="form-input"
                        placeholder="Enter city"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Pin Code</label>
                      <input
                        type="text"
                        value={address.pinCode}
                        onChange={(e) => handleAddressChange(address.id, 'pinCode', e.target.value)}
                        className="form-input"
                        placeholder="Enter pin code"
                      />
                    </div>
                  </div>
                  <div className="pay-grid pay-grid-4 pay-follow">
                    <div className="form-group">
                      <label className="form-label required">Address Line 1</label>
                      <input
                        type="text"
                        value={address.addressLine1}
                        onChange={(e) => handleAddressChange(address.id, 'addressLine1', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 1"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Address Line 2</label>
                      <input
                        type="text"
                        value={address.addressLine2}
                        onChange={(e) => handleAddressChange(address.id, 'addressLine2', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 2"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Address Line 3</label>
                      <input
                        type="text"
                        value={address.addressLine3}
                        onChange={(e) => handleAddressChange(address.id, 'addressLine3', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 3"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Address Line 4</label>
                      <input
                        type="text"
                        value={address.addressLine4}
                        onChange={(e) => handleAddressChange(address.id, 'addressLine4', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 4"
                      />
                    </div>
                  </div>
                </section>
              ))}
            </div>
          )}

          {/* Contact Details Tab */}
          {activeTab === 'contactDetails' && (
            <div className="tab-content">
              {contacts.map((contact, index) => (
                <section key={contact.id} className="pay-block">
                  <div className="pay-block-head">
                    <div>
                      <h3>{index === 0 ? 'Primary contact' : `Contact ${index + 1}`}</h3>
                      <p>{index === 0 ? 'The person we write to first.' : 'Another person at this customer.'}</p>
                    </div>
                    {index === 0 && (
                      <button 
                        type="button" 
                        className="add-contact-button"
                        onClick={addContact}
                      >
                        <Plus size={16} />
                      </button>
                    )}
                    {index > 0 && (
                      <button 
                        type="button" 
                        className="remove-contact-button"
                        onClick={() => removeContact(contact.id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="pay-grid pay-grid-4">
                    <div className="form-group">
                      <label className="form-label required">Contact Person</label>
                      <input
                        type="text"
                        value={contact.contactPerson}
                        onChange={(e) => handleContactChange(contact.id, 'contactPerson', e.target.value)}
                        className="form-input"
                        placeholder="Enter contact person name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Mobile</label>
                      <input
                        type="tel"
                        value={contact.mobile}
                        onChange={(e) => handleContactChange(contact.id, 'mobile', e.target.value)}
                        className="form-input"
                        placeholder="Enter mobile number"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Alternate Phone</label>
                      <input
                        type="tel"
                        value={contact.alternatePhone}
                        onChange={(e) => handleContactChange(contact.id, 'alternatePhone', e.target.value)}
                        className="form-input"
                        placeholder="Enter alternate phone"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Email</label>
                      <input
                        type="email"
                        value={contact.email}
                        onChange={(e) => handleContactChange(contact.id, 'email', e.target.value)}
                        className="form-input"
                        placeholder="Enter email address"
                      />
                    </div>
                  </div>
                  <div className="pay-grid pay-grid-2 pay-follow">
                    <div className="form-group">
                      <label className="form-label">Department</label>
                      <input
                        type="text"
                        value={contact.department}
                        onChange={(e) => handleContactChange(contact.id, 'department', e.target.value)}
                        className="form-input"
                        placeholder="Enter department"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Designation</label>
                      <input
                        type="text"
                        value={contact.designation}
                        onChange={(e) => handleContactChange(contact.id, 'designation', e.target.value)}
                        className="form-input"
                        placeholder="Enter designation"
                      />
                    </div>
                  </div>
                </section>
              ))}
            </div>
          )}

          {/* Action Buttons - Available in all tabs */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
            >
              <Save size={16} />
              Save
            </button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Customer Master Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default CustomerMasterForm